import type { Document } from "../document/Document";
import type { Command } from "./Command";

// Runs a sequence of sub-commands as a SINGLE undoable history entry.
//
// Without this, a user gesture that fans out into N commands (e.g. deleting a
// multi-selection, drag-reordering several layers at once) pushes N separate
// entries onto the history stack, so undoing the gesture takes N Ctrl+Z presses
// instead of one — contrary to Photoshop and to this codebase's own convention
// (GroupLayersCommand / MergeSelectedLayersCommand already operate atomically on
// a whole set). CompositeCommand wraps the sub-commands so the whole gesture is
// one atomic apply/undo.
//
// Undo is snapshot-based: it restores the exact document that existed before the
// batch ran. This is deliberate — composing each sub-command's own invert() is
// fragile when the sub-commands interact (e.g. two DeleteLayerCommands on
// adjacent layers each capture a sibling-relative restore position that no
// longer composes once both layers are gone). Since every mutation in this model
// is an immutable document replacement, the pre-batch document fully captures the
// prior state and is the correct, order-independent thing to restore.
export class CompositeCommand implements Command {
  readonly kind = "composite";
  private beforeDoc: Document | null = null;

  constructor(private readonly subs: readonly Command[], readonly label?: string) {}

  apply(doc: Document): Document {
    this.beforeDoc = doc;
    let d = doc;
    for (const c of this.subs) d = c.apply(d);
    return d;
  }

  invert(before: Document, _after: Document): Command {
    const snapshot = this.beforeDoc ?? before;
    const self = this;
    return {
      kind: "composite-restore",
      apply: (): Document => snapshot,
      invert: (): Command => self, // redo re-runs the batch
    };
  }
}
